import React, { useCallback, useMemo, useState } from "react";

import KanbanColumnView from "./KanbanColumnView";
import type { Player } from "@/features/player-kanban/types/types";

/**
 *
 */
type SortOption = "name" | "age" | "gpr" | "contract" | "rank";

/**
 * Props for KanbanColumnContainer component.
 * @remarks
 * This interface defines the container props that manage business logic for Kanban columns.
 * All drag-and-drop interactions use typed DragData and DragEvent from the feature types module.
 */
interface KanbanColumnContainerProps {
  /**
   * Unique identifier for this column
   */
  columnId: string;
  /**
   * Column header title text
   */
  title: string;
  /**
   * Total number of players in column
   */
  count: number;
  /**
   * Array of Player objects to display
   */
  players: Player[];
  /**
   * Whether column has no players
   */
  isEmpty?: boolean;
  /**
   * Text message shown when column is empty
   */
  emptyText?: string;
  /**
   * Optional board identifier
   */
  boardId?: string;
  /**
   * Numeric trigger for board refresh
   */
  boardRefreshTrigger?: number;
  /**
   * Whether any drag operation is active
   */
  isGloballyDragging?: boolean;
  /**
   * Whether user can edit column title
   */
  canEdit?: boolean;
  /**
   * Whether user can delete column
   */
  canDelete?: boolean;
  /**
   * Callback when column title changes
   */
  onTitleChange?: (newTitle: string) => void;
  /**
   * Callback when player is dropped into column
   */
  onPlayerDrop?: (playerData: unknown) => void;
  /**
   * Callback when player card is clicked
   */
  onPlayerClick?: (playerId: string) => void;
  /**
   * Callback when drag starts from this column
   */
  onGlobalDragStart?: (draggedData?: unknown, event?: unknown) => void;
  /**
   * Callback when drag operation ends
   */
  onGlobalDragEnd?: () => void;
  /**
   * Callback to trigger auto-scrolling
   */
  onAutoScroll?: (dragX: number) => void;
  /**
   * Callback when tag is added to player
   */
  onTagAdded?: (playerId: string, tag: string) => void;
  /**
   * Function to retrieve player tags
   */
  getPlayerTags?: (playerId: string) => string[];
  /**
   * Callback to refresh board data
   */
  onBoardRefresh?: () => void;
  /**
   * Callback to delete column
   */
  onDelete?: () => void;
}

/**
 * KanbanColumnContainer - Manages business logic for individual Kanban columns including player sorting, title editing, and drag interactions
 * @param root0 - The component props
 * @param root0.columnId - Unique identifier for this column used for drag/drop operations and data management
 * @param root0.title - Column header title text displayed to the user
 * @param root0.count - Total number of players in this column for badge display
 * @param root0.players - Array of Player objects to display in this column
 * @param root0.isEmpty - Boolean flag indicating whether column has no players, defaults to false
 * @param root0.emptyText - Text message displayed when column is empty, defaults to "No players"
 * @param root0.boardId - Optional board identifier used for board-specific operations and refresh triggers
 * @param root0.boardRefreshTrigger - Optional numeric trigger value that when changed causes board data to refresh
 * @param root0.isGloballyDragging - Boolean flag indicating whether any drag operation is currently active across the board, defaults to false
 * @param root0.canEdit - Boolean flag indicating whether user has permission to edit column title, defaults to false
 * @param root0.canDelete - Boolean flag indicating whether user has permission to delete the column, defaults to false
 * @param root0.onTitleChange - Callback function invoked when column title is saved, receives new title string
 * @param root0.onPlayerDrop - Callback function invoked when a player is dropped into this column, receives player data object
 * @param root0.onPlayerClick - Callback function invoked when a player card is clicked, receives player ID string
 * @param root0.onGlobalDragStart - Callback function invoked when drag operation starts from this column, receives dragged data and event
 * @param root0.onGlobalDragEnd - Callback function invoked when drag operation ends
 * @param root0.onAutoScroll - Callback function invoked to trigger horizontal auto-scrolling during drag, receives drag X coordinate
 * @param root0.onTagAdded - Callback function invoked when a tag is added to a player, receives player ID and tag string
 * @param root0.getPlayerTags - Callback function that retrieves tags for a specific player, receives player ID and returns array of tag strings
 * @param root0.onBoardRefresh - Callback function invoked when board data should be refreshed
 * @param root0.onDelete - Callback function invoked when column should be deleted
 */
const KanbanColumnContainer = ({
  columnId,
  title,
  count,
  players,
  isEmpty = false,
  emptyText = "No players",
  boardId,
  boardRefreshTrigger,
  isGloballyDragging = false,
  canEdit = false,
  canDelete = false,
  onTitleChange,
  onPlayerDrop,
  onPlayerClick,
  onGlobalDragStart,
  onGlobalDragEnd,
  onAutoScroll,
  onTagAdded,
  getPlayerTags,
  onBoardRefresh,
  onDelete,
}: KanbanColumnContainerProps) => {
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editTitleValue, setEditTitleValue] = useState(title);
  const [sortBy, setSortBy] = useState<SortOption>("rank");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  // SE-4684: Rank is column-scoped and derived from the incoming (already
  // viewer-ordered) players array — generateColumnsData sorts each phase's cards
  // ascending before they reach this column. Assigning rank BEFORE the
  // per-column re-sort keeps each card's rank stable regardless of which sort is
  // active (AC: rank badge stays visible and unchanged when Name/Age/GPR/Contract
  // sort is applied).
  //
  // SE-5493: on a PIPELINE board `generateColumnsData` has already stamped the
  // viewer's server-resolved `rank` onto each player, and ordered the column by
  // it — so the badge is that number, not a re-derived ordinal. The ordinal is
  // kept as the fallback for ROSTER/WATCHLIST columns (no per-user ordering
  // exists there) and for any card whose rank has not been resolved yet. The
  // "assign before re-sort" property above is unchanged either way: both
  // branches read only `players`, never `sortedPlayers`.
  const playersWithRank = useMemo(
    () =>
      players.map((player, index) => ({
        ...player,
        rank: player.rank ?? index + 1,
      })),
    [players]
  );

  const sortedPlayers = useMemo(() => {
    // Rank sort restores the ascending rank order (rank 1 at the top).
    if (sortBy === "rank") return playersWithRank;

    return [...playersWithRank].sort((a, b) => {
      const comparison = (() => {
        switch (sortBy) {
          case "name":
            return a.name.localeCompare(b.name);
          case "age":
            return (a.age || 0) - (b.age || 0);
          case "gpr":
            return (a.gpr || 0) - (b.gpr || 0);
          case "contract": {
            const aValue =
              parseFloat(a.contractValue.replace(/[^0-9.-]/g, "")) || 0;
            const bValue =
              parseFloat(b.contractValue.replace(/[^0-9.-]/g, "")) || 0;
            return aValue - bValue;
          }
          default:
            return 0;
        }
      })();

      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [playersWithRank, sortBy, sortDirection]);

  const handleSort = useCallback(
    (option: SortOption) => {
      // Rank is the canonical ascending order (rank 1 at the top) and is not
      // directionally toggleable — reselecting it must not flip to descending.
      if (option === "rank") {
        setSortBy("rank");
        setSortDirection("asc");
        return;
      }

      if (option === sortBy) {
        setSortDirection(prev => (prev === "asc" ? "desc" : "asc"));
      } else {
        setSortBy(option);
        setSortDirection("asc");
      }
    },
    [sortBy]
  );

  const handlePlayerClick = useCallback(
    (playerId: string) => {
      onPlayerClick?.(playerId);
    },
    [onPlayerClick]
  );

  const handleStartTitleEdit = useCallback(() => {
    setEditTitleValue(title);
    setIsEditingTitle(true);
  }, [title]);

  const handleSaveTitleEdit = useCallback(() => {
    if (editTitleValue.trim()) {
      onTitleChange?.(editTitleValue.trim());
    } else {
      setEditTitleValue(title);
    }
    setIsEditingTitle(false);
  }, [editTitleValue, title, onTitleChange]);

  const handleCancelTitleEdit = useCallback(() => {
    setEditTitleValue(title);
    setIsEditingTitle(false);
  }, [title]);

  const handleTitleChange = useCallback((value: string) => {
    setEditTitleValue(value);
  }, []);

  const [isDraggingFromThisColumn, setIsDraggingFromThisColumn] =
    useState(false);

  const handleLocalDragStart = useCallback(
    (draggedData?: unknown, event?: unknown) => {
      console.log(
        "🏛️ [KANBAN COLUMN] Local drag start, passing to global:",
        draggedData
      );
      setIsDraggingFromThisColumn(true);
      onGlobalDragStart?.(draggedData, event);
    },
    [onGlobalDragStart]
  );

  const handleLocalDragEnd = useCallback(() => {
    setIsDraggingFromThisColumn(false);
    onGlobalDragEnd?.();
  }, [onGlobalDragEnd]);

  /*
   * SE-6554 (Figma 80:16392, "Column · Drag-over") — is the dragged card
   * currently aimed at THIS lane?
   *
   * The state has to live at the column level because the design draws the
   * drag-over outline around the WHOLE column, stage name included, while the
   * droppable that knows the answer is registered one level down in the content
   * area below the header. So the content component reports its `isOver` up
   * here rather than this component asking dnd-kit the same question again:
   * there is exactly one droppable and exactly one answer, and the outline
   * cannot disagree with the drop gap that answer also drives.
   */
  const [isDragOver, setIsDragOver] = useState(false);

  // SE-4685: within-column drag-to-reorder is active only when the column is
  // sorted by Rank and the user can edit — the grab handle appears and the
  // cards become sortable only in that state.
  const isRankReorderEnabled = sortBy === "rank" && canEdit;

  return (
    <KanbanColumnView
      _columnId={columnId}
      title={title}
      count={count}
      players={sortedPlayers}
      isEmpty={isEmpty}
      emptyText={emptyText}
      boardId={boardId}
      boardRefreshTrigger={boardRefreshTrigger}
      isEditingTitle={isEditingTitle}
      editTitleValue={editTitleValue}
      isGloballyDragging={isGloballyDragging}
      isDraggingFromThisColumn={isDraggingFromThisColumn}
      isDragOver={isDragOver}
      onDragOverChange={setIsDragOver}
      sortBy={sortBy}
      sortDirection={sortDirection}
      canEdit={canEdit}
      canDelete={canDelete}
      isRankReorderEnabled={isRankReorderEnabled}
      onPlayerClick={handlePlayerClick}
      onStartTitleEdit={handleStartTitleEdit}
      onSaveTitleEdit={handleSaveTitleEdit}
      onCancelTitleEdit={handleCancelTitleEdit}
      onTitleChange={handleTitleChange}
      onPlayerDrop={onPlayerDrop}
      onLocalDragStart={handleLocalDragStart}
      onLocalDragEnd={handleLocalDragEnd}
      onAutoScroll={onAutoScroll}
      onSort={handleSort}
      onDelete={onDelete}
      _onTagAdded={onTagAdded}
      _getPlayerTags={getPlayerTags}
      onBoardRefresh={onBoardRefresh}
    />
  );
};

export default React.memo(KanbanColumnContainer);
